import * as PIXI from 'pixi.js';
import { ColorMatrixFilter } from '@pixi/filter-color-matrix';
import { Config } from '../../../video-data/config';

import { BooleanAttribute, ColorAttribute, getAttributesToSerialize } from '../../attributes';
import { rgb2int } from '../../../libs/videoData';
import { Filter } from '../Filter';
import type { BaseSettings } from '../../TimeAwareBase';

export class ColorChannelMapFilter extends Filter {
    override _classType = Config.COLOR_CHANNEL_MAP_FILTER;
    override pixiFilter: ColorMatrixFilter;

    @ColorAttribute()
    accessor redChannelTargetColor = rgb2int([255, 0, 0]);
    @ColorAttribute()
    accessor greenChannelTargetColor = rgb2int([0, 255, 0]);
    @ColorAttribute()
    accessor blueChannelTargetColor = rgb2int([0, 0, 255]);
    @BooleanAttribute()
    accessor isRedChannelMappedToAlpha = false;
    @BooleanAttribute()
    accessor isGreenChannelMappedToAlpha = false;
    @BooleanAttribute()
    accessor isBlueChannelMappedToAlpha = false;

    constructor(settings: BaseSettings) {
        super(settings);
        this.pixiFilter = new ColorMatrixFilter();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    override _update() {
        const [target1R, target1G, target1B] = PIXI.utils.hex2rgb(this.redChannelTargetColor);
        const [target2R, target2G, target2B] = PIXI.utils.hex2rgb(this.greenChannelTargetColor);
        const [target3R, target3G, target3B] = PIXI.utils.hex2rgb(this.blueChannelTargetColor);

        const isAlphaApplied =
            this.isRedChannelMappedToAlpha ||
            this.isGreenChannelMappedToAlpha ||
            this.isBlueChannelMappedToAlpha;
        this.pixiFilter.matrix = [
            this.isRedChannelMappedToAlpha ? 0 : target1R,
            this.isGreenChannelMappedToAlpha ? 0 : target2R,
            this.isBlueChannelMappedToAlpha ? 0 : target3R,
            0,
            0,
            this.isRedChannelMappedToAlpha ? 0 : target1G,
            this.isGreenChannelMappedToAlpha ? 0 : target2G,
            this.isBlueChannelMappedToAlpha ? 0 : target3G,
            0,
            0,
            this.isRedChannelMappedToAlpha ? 0 : target1B,
            this.isGreenChannelMappedToAlpha ? 0 : target2B,
            this.isBlueChannelMappedToAlpha ? 0 : target3B,
            0,
            0,
            this.isRedChannelMappedToAlpha ? 1 : 0,
            this.isGreenChannelMappedToAlpha ? 1 : 0,
            this.isBlueChannelMappedToAlpha ? 1 : 0,
            isAlphaApplied ? 0 : 1,
            0,
        ];
    }

    override isNeutral(): boolean {
        return (
            this.redChannelTargetColor === rgb2int([255, 0, 0]) &&
            this.greenChannelTargetColor === rgb2int([0, 255, 0]) &&
            this.blueChannelTargetColor === rgb2int([0, 0, 255]) &&
            this.isRedChannelMappedToAlpha === false &&
            this.isGreenChannelMappedToAlpha === false &&
            this.isBlueChannelMappedToAlpha === false
        );
    }
}
